import { useCallback, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight, ChevronRight, Copy, KeyRound, Plus, RotateCcw, Send, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  PageLoader,
  SectionTitle,
  TextField,
  Toggle,
  cx,
} from "@/components/ui";
import { Avatar } from "@/components/bits";
import { useToast } from "@/components/Toast";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { useAction, useGrants, useIntegrations, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { ClaudeConnectorCard } from "./ClaudeConnector";
import type { ApiTokenDTO, BankEntryDTO, IntegrationDTO, RewardTypeDTO } from "@shared/types";

// ---------- local helpers ----------

function useCopy() {
  const toast = useToast();
  return async (text: string, what = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.show(what, "success");
    } catch {
      toast.show("Couldn't copy — press and hold to copy it instead.", "error");
    }
  };
}

function CopyRow({ label, value, copied, secret }: { label?: ReactNode; value: string; copied?: string; secret?: boolean }) {
  const copy = useCopy();
  return (
    <div className="space-y-1.5">
      {label && <div className="text-sm font-bold">{label}</div>}
      <div className={cx("flex items-center gap-2 rounded-2xl py-1.5 pl-3 pr-1.5 ring-1", secret ? "bg-coin-soft ring-coin/40" : "bg-sunken ring-line")}>
        <code className="min-w-0 flex-1 select-all break-all font-mono text-sm">{value}</code>
        <Button variant="secondary" size="sm" className="shrink-0" onClick={() => copy(value, copied)}>
          <Copy className="size-4" /> Copy
        </Button>
      </div>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-2xl bg-sunken p-3 font-mono text-xs leading-relaxed ring-1 ring-line">{children}</pre>
  );
}

type Provider = IntegrationDTO["provider"];

const PROVIDERS: Record<Provider, { icon: string; label: string; blurb: string; account: string }> = {
  xbox: {
    icon: "🎮",
    label: "Xbox (via bridge)",
    blurb: "Xbox Family Settings, applied by a bridge",
    account: "Gamertag",
  },
  nintendo: {
    icon: "🕹️",
    label: "Nintendo Switch (via bridge)",
    blurb: "Nintendo parental controls, applied by a bridge",
    account: "Nintendo account",
  },
  webhook: {
    icon: "🔗",
    label: "Webhook",
    blurb: "We POST each grant to a URL you run, signed",
    account: "Account",
  },
  mcp: {
    icon: "🤖",
    label: "MCP agent",
    blurb: "Any MCP-capable agent or automation",
    account: "Account",
  },
};
const PROVIDER_ORDER: Provider[] = ["xbox", "nintendo", "webhook", "mcp"];

const MCP_TOOLS: { name: string; does: string }[] = [
  { name: "list_kids", does: "Kids and their linked console accounts" },
  { name: "get_time_rules", does: "Time rewards with caps and schedules" },
  { name: "get_time_status", does: "Earned time and cap usage per kid" },
  { name: "start_time_session", does: "Start a session — checked against caps & schedules" },
  { name: "list_pending_grants", does: "Time waiting to be applied on a device" },
  { name: "mark_grant_applied", does: "Report that a grant was applied (or failed)" },
];

function mcpConfig(url: string, token = "<token>") {
  return JSON.stringify(
    { mcpServers: { "meaningful-rewards": { type: "http", url, headers: { Authorization: `Bearer ${token}` } } } },
    null,
    2,
  );
}

interface Draft {
  id?: string;
  provider: Provider;
  name: string;
  url: string;
  accounts: Record<string, string>;
  enabled: boolean;
}

type Created = IntegrationDTO & { secret?: string };
type TestResult = { ok: boolean; status?: number; error?: string };

function draftFrom(i: IntegrationDTO): Draft {
  return { id: i.id, provider: i.provider, name: i.name, url: i.url ?? "", accounts: { ...i.accounts }, enabled: i.enabled };
}

function cleanAccounts(accounts: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(accounts)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v),
  );
}

const sameAccounts = (a: Record<string, string>, b: Record<string, string>) => {
  const ca = cleanAccounts(a);
  const cb = cleanAccounts(b);
  const keys = new Set([...Object.keys(ca), ...Object.keys(cb)]);
  return [...keys].every((k) => ca[k] === cb[k]);
};

// ---------- page ----------

export function Integrations() {
  const { data, isLoading, error, refetch } = useIntegrations();
  const { data: rewardData } = useRewardTypes();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [secret, setSecret] = useState<{ name: string; secret: string; url: string | null } | null>(null);
  // Stable close handlers: the Sheet re-runs its open effect (and refocuses) when onClose changes.
  const closeDraft = useCallback(() => setDraft(null), []);
  const closeSecret = useCallback(() => setSecret(null), []);

  const rewardTypes = rewardData?.rewardTypes ?? [];
  const linkedRewards = (id: string) => rewardTypes.filter((r) => r.integrationId === id);

  const save = useAction(
    (d: Draft) => {
      const accounts = cleanAccounts(d.accounts);
      if (!d.id) {
        return api.post<Created>("/integrations", {
          provider: d.provider,
          name: d.name.trim(),
          url: d.provider === "webhook" ? d.url.trim() : undefined,
          accounts,
        });
      }
      const original = data?.integrations.find((i) => i.id === d.id);
      const body: Record<string, unknown> = {};
      if (!original || d.name.trim() !== original.name) body.name = d.name.trim();
      if (d.provider === "webhook" && (!original || d.url.trim() !== (original.url ?? ""))) body.url = d.url.trim();
      if (!original || !sameAccounts(d.accounts, original.accounts)) body.accounts = accounts;
      if (!original || d.enabled !== original.enabled) body.enabled = d.enabled;
      return api.patch<Created>(`/integrations/${d.id}`, body);
    },
    {
      onSuccess: (res, d) => {
        setDraft(null);
        toast.show(d.id ? "Saved" : `${res.name} connected`, "success");
        if (!d.id && res.secret) setSecret({ name: res.name, secret: res.secret, url: res.url });
      },
    },
  );

  const remove = useAction((i: IntegrationDTO) => api.del(`/integrations/${i.id}`), {
    success: "Integration removed",
    onSuccess: () => setDraft(null),
  });

  const test = useAction((i: IntegrationDTO) => api.post<TestResult>(`/integrations/${i.id}/test`), {
    success: (r) =>
      r.ok ? (r.status ? `Test delivered — your endpoint answered ${r.status}` : "All set — bridges pull time through MCP") : null,
    onSuccess: (r) => {
      if (!r.ok) toast.show(`Test failed: ${r.error ?? "no response"}`, "error");
    },
  });

  if (isLoading) return <PageLoader />;
  if (error || !data) {
    return (
      <div>
        <PageHeader title="Integrations" back="/parent/more" />
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  const list = data.integrations;
  const live = draft?.id ? list.find((i) => i.id === draft.id) : undefined;
  const newDraft = (): Draft => ({ provider: "xbox", name: "Xbox", url: "", accounts: {}, enabled: true });

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Automatic time enforcement"
        back="/parent/more"
        actions={
          <Button size="sm" onClick={() => setDraft(newDraft())}>
            <Plus className="size-4" /> Add
          </Button>
        }
      />

      <ClaudeConnectorCard />

      <SectionTitle>Game time</SectionTitle>
      <IntroCard />

      <SectionTitle>Connections</SectionTitle>
      {list.length === 0 ? (
        <EmptyState icon="🎮" title="Nothing connected yet" action={<Button onClick={() => setDraft(newDraft())}>Connect a console</Button>}>
          Hook up a bridge so earned screen time turns on — and off — by itself.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {list.map((i) => (
            <IntegrationCard key={i.id} integration={i} linked={linkedRewards(i.id)} onOpen={() => setDraft(draftFrom(i))} />
          ))}
        </div>
      )}
      <LinkHint className="mt-3" />

      <McpSection mcpUrl={data.mcpUrl} tokens={data.tokens} />

      <GrantsLog rewardTypes={rewardTypes} />

      {/* Add / edit */}
      <Sheet
        open={!!draft}
        onClose={closeDraft}
        title={draft?.id ? "Edit integration" : "Connect"}
        size="lg"
        footer={
          <div className="flex gap-2">
            {live && (
              <Button
                variant="secondary"
                aria-label="Delete integration"
                loading={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Remove ${live.name}? Linked time rewards will stop syncing (they keep working in the app).`)) {
                    remove.mutate(live);
                  }
                }}
              >
                <Trash2 className="size-4 text-danger" />
              </Button>
            )}
            {live && (
              <Button variant="secondary" loading={test.isPending} onClick={() => test.mutate(live)}>
                <Send className="size-4" /> Test
              </Button>
            )}
            <Button
              block
              onClick={() => draft && save.mutate(draft)}
              loading={save.isPending}
              disabled={!draft?.name.trim() || (draft.provider === "webhook" && !draft.url.trim())}
            >
              {draft?.id ? "Save" : "Connect"}
            </Button>
          </div>
        }
      >
        {draft && <IntegrationForm draft={draft} setDraft={setDraft} live={live} linked={live ? linkedRewards(live.id) : []} />}
      </Sheet>

      {/* Webhook signing secret — shown once */}
      <Sheet
        open={!!secret}
        onClose={closeSecret}
        title="Save your signing secret"
        footer={
          <Button block onClick={closeSecret}>
            I've saved it
          </Button>
        }
      >
        {secret && (
          <div className="space-y-4 pb-2">
            <p className="rounded-2xl bg-warn-soft px-3 py-2 text-sm font-semibold text-warn">
              This is the only time you'll see it. Copy it into your webhook receiver now.
            </p>
            <CopyRow label={`Secret for ${secret.name}`} value={secret.secret} copied="Secret copied" secret />
            <div className="space-y-2 text-sm">
              <p className="text-muted">
                Each grant is POSTed as JSON to {secret.url ? <b className="break-all text-ink">{secret.url}</b> : "your URL"} with these
                headers:
              </p>
              <CodeBlock>{`content-type: application/json
x-mr-event: time.granted
x-mr-signature: sha256=HMAC_SHA256(secret, body)`}</CodeBlock>
              <p className="text-muted">
                Compute the HMAC-SHA256 of the raw request body with this secret (hex) and compare it to the signature before applying
                any time. Use <b className="text-ink">Test</b> to send a sample <code className="font-mono">test</code> event.
              </p>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

// ---------- intro ----------

function IntroCard() {
  return (
    <Card className="mt-2 space-y-3">
      <div className="flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-violet-soft text-2xl">⚡</span>
        <div>
          <div className="font-extrabold">Earned time, applied automatically</div>
          <p className="mt-0.5 text-sm text-muted">
            Screen time kids earn can be switched on for their game systems automatically. Microsoft (Xbox Family Settings) and
            Nintendo don't offer public APIs, so a <b className="text-ink">bridge</b> connects them — either a webhook receiver you run,
            or any MCP-capable agent or automation using the MCP endpoint below.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 overflow-x-auto rounded-2xl bg-sunken px-3 py-2.5 text-xs font-bold text-muted no-scrollbar">
        <span className="shrink-0">🧒 Kid starts time</span>
        <ArrowRight className="size-3.5 shrink-0" />
        <span className="shrink-0 text-brand">✅ Caps & schedule checked here</span>
        <ArrowRight className="size-3.5 shrink-0" />
        <span className="shrink-0">🎮 Bridge applies it</span>
      </div>
      <p className="text-xs text-muted">Caps and schedules from your time rewards are always enforced here first.</p>
    </Card>
  );
}

function LinkHint({ className }: { className?: string }) {
  return (
    <p className={cx("px-1 text-sm text-muted", className)}>
      Link a time reward to an integration in{" "}
      <Link to="/parent/plan/rewards" className="font-bold text-brand">
        Plan → Rewards
      </Link>
      .
    </p>
  );
}

// ---------- integration card & form ----------

function IntegrationCard({ integration: i, linked, onOpen }: { integration: IntegrationDTO; linked: RewardTypeDTO[]; onOpen: () => void }) {
  const p = PROVIDERS[i.provider];
  return (
    <Card
      className="cursor-pointer p-3 transition hover:ring-brand/40"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
    >
      <div className="flex items-start gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-sunken text-2xl">{p.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-extrabold">{i.name}</span>
            {i.enabled ? <Badge tone="brand">On</Badge> : <Badge>Paused</Badge>}
          </div>
          <div className="truncate text-sm text-muted">
            {p.label} · {i.lastEventAt ? `Last event ${timeAgo(i.lastEventAt)}` : "No events yet"}
          </div>
          {i.lastError && <p className="mt-1 line-clamp-2 text-sm font-semibold text-danger">⚠️ {i.lastError}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {linked.length ? (
              linked.map((r) => (
                <Badge key={r.id} tone="violet">
                  {r.icon} {r.name}
                </Badge>
              ))
            ) : (
              <span className="text-xs font-semibold text-faint">No time reward linked yet</span>
            )}
          </div>
        </div>
        <ChevronRight className="mt-3 size-5 shrink-0 text-faint" />
      </div>
    </Card>
  );
}

function IntegrationForm({
  draft,
  setDraft,
  live,
  linked,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  live: IntegrationDTO | undefined;
  linked: RewardTypeDTO[];
}) {
  const { kids } = useSession();
  const p = PROVIDERS[draft.provider];

  return (
    <div className="space-y-5">
      {draft.id ? (
        <div className="flex items-center gap-3 rounded-2xl bg-sunken px-3 py-2.5">
          <span className="text-2xl">{p.icon}</span>
          <div className="min-w-0">
            <div className="font-extrabold">{p.label}</div>
            <div className="text-xs text-muted">
              {live?.lastEventAt ? `Last event ${timeAgo(live.lastEventAt)}` : "No events yet"}
              {live?.provider === "webhook" && live.hasSecret ? " · Signing secret set" : ""}
            </div>
          </div>
        </div>
      ) : (
        <Field label="What are you connecting?">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {PROVIDER_ORDER.map((key) => {
              const opt = PROVIDERS[key];
              const active = draft.provider === key;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    const wasDefault = !draft.name.trim() || PROVIDER_ORDER.some((k) => defaultName(k) === draft.name.trim());
                    setDraft({ ...draft, provider: key, name: wasDefault ? defaultName(key) : draft.name });
                  }}
                  className={cx(
                    "flex items-center gap-3 rounded-2xl px-3 py-3 text-left transition",
                    active ? "bg-brand-soft ring-2 ring-brand" : "bg-sunken ring-1 ring-line hover:ring-brand/50",
                  )}
                >
                  <span className="text-2xl">{opt.icon}</span>
                  <span className="min-w-0">
                    <span className="block font-extrabold leading-tight">{opt.label}</span>
                    <span className="block text-xs text-muted">{opt.blurb}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {live?.lastError && (
        <p className="rounded-2xl bg-danger-soft px-3 py-2 text-sm font-semibold text-danger">Last error: {live.lastError}</p>
      )}

      <TextField label="Name" value={draft.name} maxLength={60} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />

      {draft.provider === "webhook" && (
        <TextField
          label="Webhook URL"
          type="url"
          inputMode="url"
          placeholder="https://bridge.example.com/meaningful-rewards"
          value={draft.url}
          onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          hint="Must be a public https:// address. Each grant is POSTed here as signed JSON."
        />
      )}

      {draft.provider !== "webhook" && !draft.id && (
        <p className="rounded-2xl bg-violet-soft px-3 py-2 text-sm text-violet">
          Bridges pull earned time through the MCP endpoint. After connecting, create an API token below for your bridge or agent.
        </p>
      )}

      {kids.length > 0 && (
        <Field label="Accounts" hint="Tells the bridge which console profile belongs to which kid. Leave blank to skip a kid.">
          <div className="space-y-2">
            {kids.map((k) => (
              <div key={k.id} className="flex items-center gap-2">
                <Avatar member={k} size="sm" />
                <Input
                  className="min-w-0 flex-1"
                  placeholder={`${p.account} for ${k.name}`}
                  aria-label={`Gamertag / account for ${k.name}`}
                  autoComplete="off"
                  value={draft.accounts[k.id] ?? ""}
                  maxLength={100}
                  onChange={(e) => setDraft({ ...draft, accounts: { ...draft.accounts, [k.id]: e.target.value } })}
                />
              </div>
            ))}
          </div>
        </Field>
      )}

      {draft.id && (
        <Toggle
          checked={draft.enabled}
          onChange={(enabled) => setDraft({ ...draft, enabled })}
          label="Enabled"
          description="Paused integrations don't receive new grants. Time still works in the app."
        />
      )}

      {draft.id && (
        <div className="rounded-2xl bg-sunken px-3 py-3">
          <div className="text-sm font-bold">Linked time rewards</div>
          {linked.length ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {linked.map((r) => (
                <Badge key={r.id} tone="violet">
                  {r.icon} {r.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="mt-0.5 text-sm text-muted">None yet.</p>
          )}
          <LinkHint className="mt-1.5 px-0" />
        </div>
      )}
    </div>
  );
}

function defaultName(p: Provider) {
  return { xbox: "Xbox", nintendo: "Nintendo Switch", webhook: "Webhook", mcp: "Home automation" }[p];
}

// ---------- MCP & tokens ----------

function McpSection({ mcpUrl, tokens }: { mcpUrl: string; tokens: ApiTokenDTO[] }) {
  const copy = useCopy();
  const [sheet, setSheet] = useState<{ name: string; created: ApiTokenDTO | null } | null>(null);
  const close = useCallback(() => setSheet(null), []);

  const create = useAction((name: string) => api.post<ApiTokenDTO>("/tokens", { name }), {
    success: "Token created",
    onSuccess: (created) => setSheet((s) => (s ? { ...s, created } : s)),
  });
  const revoke = useAction((t: ApiTokenDTO) => api.del(`/tokens/${t.id}`), { success: "Token revoked" });

  return (
    <>
      <SectionTitle>MCP endpoint</SectionTitle>
      <Card className="space-y-4">
        <p className="text-sm text-muted">
          Connect a home automation or a console bridge. It can read earned time, start sessions (checked against your caps and
          schedules) and report when time was applied. It only sees time rewards — never chores or family activity.
        </p>
        <CopyRow label="Endpoint URL" value={mcpUrl} copied="MCP URL copied" />
        <div className="space-y-1.5">
          <div className="flex items-end justify-between gap-2">
            <div className="text-sm font-bold">Client config</div>
            <Button variant="ghost" size="sm" onClick={() => copy(mcpConfig(mcpUrl), "Config copied")}>
              <Copy className="size-4" /> Copy
            </Button>
          </div>
          <CodeBlock>{mcpConfig(mcpUrl)}</CodeBlock>
          <p className="text-xs text-muted">
            Replace <code className="font-mono">&lt;token&gt;</code> with an API token from below.
          </p>
        </div>
        <div>
          <div className="mb-1.5 text-sm font-bold">Tools available</div>
          <ul className="grid gap-1.5 sm:grid-cols-2">
            {MCP_TOOLS.map((t) => (
              <li key={t.name} className="rounded-2xl bg-sunken px-3 py-2">
                <code className="font-mono text-xs font-bold text-brand-strong">{t.name}</code>
                <div className="text-xs text-muted">{t.does}</div>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <SectionTitle
        action={
          <Button size="sm" variant="soft" onClick={() => setSheet({ name: "", created: null })}>
            <KeyRound className="size-4" /> New token
          </Button>
        }
      >
        API tokens
      </SectionTitle>
      {tokens.length === 0 ? (
        <Card className="text-sm text-muted">No tokens yet. Create one for each bridge or agent so you can revoke them separately.</Card>
      ) : (
        <Card className="p-1.5">
          <ul className="divide-y divide-line/70">
            {tokens.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-3 py-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-sunken text-xl">🔑</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-extrabold">{t.name}</div>
                  <div className="truncate text-xs text-muted">
                    <code className="font-mono">{t.prefix}…</code> · {t.lastUsedAt ? `Used ${timeAgo(t.lastUsedAt)}` : "Never used"} · Created{" "}
                    {new Date(t.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
                <IconButton
                  label={`Revoke ${t.name}`}
                  disabled={revoke.isPending}
                  onClick={() => {
                    if (window.confirm(`Revoke “${t.name}”? Anything using it stops working right away.`)) revoke.mutate(t);
                  }}
                >
                  <Trash2 className="size-5 text-danger" />
                </IconButton>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Sheet
        open={!!sheet}
        onClose={close}
        title={sheet?.created ? "Copy your token" : "New API token"}
        footer={
          sheet?.created ? (
            <Button block onClick={close}>
              I've saved it
            </Button>
          ) : (
            <Button
              block
              loading={create.isPending}
              disabled={!sheet?.name.trim()}
              onClick={() => sheet && create.mutate(sheet.name.trim())}
            >
              Create token
            </Button>
          )
        }
      >
        {sheet &&
          (sheet.created?.token ? (
            <div className="space-y-4 pb-2">
              <p className="rounded-2xl bg-warn-soft px-3 py-2 text-sm font-semibold text-warn">
                This is the only time you'll see this token. Keep it somewhere safe — anyone with it can manage your kids' time.
              </p>
              <CopyRow label={sheet.created.name} value={sheet.created.token} copied="Token copied" secret />
              <div className="space-y-1.5">
                <div className="flex items-end justify-between gap-2">
                  <div className="text-sm font-bold">Ready-to-paste config</div>
                  <Button variant="ghost" size="sm" onClick={() => copy(mcpConfig(mcpUrl, sheet.created!.token), "Config copied")}>
                    <Copy className="size-4" /> Copy
                  </Button>
                </div>
                <CodeBlock>{mcpConfig(mcpUrl, sheet.created.token)}</CodeBlock>
              </div>
            </div>
          ) : (
            <div className="space-y-3 pb-2">
              <TextField
                label="Name"
                placeholder="Xbox bridge on the home server"
                value={sheet.name}
                maxLength={60}
                onChange={(e) => setSheet({ ...sheet, name: e.target.value })}
                hint="So you remember what uses it."
              />
            </div>
          ))}
      </Sheet>
    </>
  );
}

// ---------- grants log ----------

const SYNC: Record<BankEntryDTO["syncStatus"], { label: string; tone: "warn" | "brand" | "danger" | "neutral" }> = {
  pending: { label: "Waiting", tone: "warn" },
  synced: { label: "Applied", tone: "brand" },
  failed: { label: "Failed", tone: "danger" },
  none: { label: "Not synced", tone: "neutral" },
};

function GrantsLog({ rewardTypes }: { rewardTypes: RewardTypeDTO[] }) {
  const { memberById } = useSession();
  const { data, isLoading } = useGrants();
  const retry = useAction(() => api.post<{ retried: number }>("/integrations/grants/retry"), {
    success: (r) => (r.retried ? `Retrying ${r.retried} ${r.retried === 1 ? "grant" : "grants"}` : "Nothing to retry"),
  });
  const grants = data?.grants ?? [];
  const failed = grants.filter((g) => g.syncStatus === "failed").length;

  return (
    <>
      <SectionTitle
        action={
          failed > 0 ? (
            <Button size="sm" variant="soft" onClick={() => retry.mutate()} loading={retry.isPending}>
              <RotateCcw className="size-4" /> Retry failed
            </Button>
          ) : undefined
        }
      >
        Recent grants
      </SectionTitle>
      {isLoading ? (
        <Card className="text-sm text-muted">Loading…</Card>
      ) : grants.length === 0 ? (
        <EmptyState icon="⏱️" title="No automatic grants yet">
          When a kid starts time from a linked reward, it shows up here with its sync status.
        </EmptyState>
      ) : (
        <Card className="p-1.5">
          <ul className="divide-y divide-line/70">
            {grants.map((g) => {
              const kid = memberById(g.kidId);
              const rt = rewardTypes.find((r) => r.id === g.rewardTypeId);
              const s = SYNC[g.syncStatus];
              return (
                <li key={g.id} className="flex items-start gap-3 px-3 py-3">
                  {kid ? <Avatar member={kid} size="sm" /> : <span className="size-8 shrink-0 rounded-full bg-sunken" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-extrabold">{kid?.name ?? "Former kid"}</span>
                      <span className="text-sm font-bold tabular-nums">
                        {rt?.icon} {Math.abs(g.amount).toLocaleString()} {rt?.unitLabel ?? "min"}
                      </span>
                      <Badge tone={s.tone}>{s.label}</Badge>
                    </div>
                    <div className="text-xs text-muted">
                      {rt?.name ?? "Time"} · {timeAgo(g.createdAt)}
                    </div>
                    {g.syncStatus === "failed" && g.syncError && (
                      <p className="mt-0.5 text-xs font-semibold text-danger">{g.syncError}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </>
  );
}
